import '@/store/api/product-inventory/typedefs'
import api from '@/store/api/product-inventory'
import * as mutationTypes from './mutation-types'
import {mapLegacyPagination} from '@/store/utils'

// Modules
import product from './product'

const initialState = () => ({
    productBillingFrequencies: {
        data: []
    },
    productCategories: {
        data: []
    },
    productInventoryTypes: {
        data: []
    },
    productStatuses: {
        data: []
    },
    productBundleTypes: {
        data: []
    },
    productTypes: {
        data: []
    },
    productFamilies: {
        data: []
    },
    productMetrics: {
        data: []
    },
    products: {
        data: [],
        pagination: {}
    }
})

export const mapProduct = product => ({
    ...product,
    metric_type_id: product.metric_type_id,
    category_id: product.product_category_id,
    inventory_type_id: product.inventory_type,
    metric_type: {
        id: product.metric_type_id,
        name: product.metric_type_name
    },
    inventory_type: {
        id: product.inventory_type_id,
        name: product.inventory_type_name
    },
    product_billing_frequency: {
        id: product.billing_frequency_id,
        name: product.billing_frequency_name
    },
    product_category: {
        id: product.product_category_id,
        name: product.category_name
    },
    product_family: {
        id: product.family_id,
        name: product.family_name
    },
    product_status: {
        id: product.status_id,
        description: product.status_description,
        name: product.status_name
    },
    product_supplier: {
        id: product.supplier_id,
        name: product.supplier_name
    },
    product_type: {
        id: product.product_type_id,
        name: product.product_type_name
    }
})

export default {
    namespaced: true,
    state: initialState(),
    modules: {
        product
    },
    getters: {
        inventoryTypes: state => state.inventoryTypes,
        productBillingFrequencies: state => state.productBillingFrequencies,
        productCategories: state => state.productCategories,
        products: state => state.products.data,
        pagination: state => state.products.pagination,
        productStatuses: state => state.productStatuses,
        productTypes: state => state.productTypes,
        productFamilies: state => state.productFamilies,
        productMetrics: state => state.productMetrics
    },
    actions: {
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchProductBillingFrequencies: async ({commit, dispatch}, query) => {
            try {
                const response = await api.billingFrequencies.searchBillingFrequencies(query)
                commit(mutationTypes.SET_PRODUCT_BILLING_FREQUENCIES, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchBundleTypes: async ({commit, dispatch}, query) => {
            try {
                const response = await api.bundleTypes.searchBundleTypes(query)
                commit(mutationTypes.SET_PRODUCT_BUNDLE_TYPES, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchProductCategories: async ({commit, dispatch}, query) => {
            try {
                const response = await api.productCategories.searchProductCategories(query)
                commit(mutationTypes.SET_PRODUCT_CATEGORIES, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchProductInventoryTypes: async ({commit, dispatch}, query) => {
            try {
                const response = await api.inventoryTypes.searchProductInventoryTypes(query)
                commit(mutationTypes.SET_PRODUCT_INVENTORY_TYPES, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchProductStatuses: async ({commit, dispatch}, query) => {
            try {
                const response = await api.productStatuses.searchProductStatuses(query)
                commit(mutationTypes.SET_PRODUCT_STATUSES, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchProductTypes: async ({commit, dispatch}, query) => {
            try {
                const response = await api.productTypes.searchProductTypes(query)
                commit(mutationTypes.SET_PRODUCT_TYPES, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchProductFamilies: async ({commit, dispatch}, query) => {
            try {
                const response = await api.productFamilies.searchProductFamilies(query)
                commit(mutationTypes.SET_PRODUCT_FAMILIES, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {SearchQuery} query
         * @returns {Promise<*>}
         */
        searchProductMetrics: async ({commit, dispatch}, query) => {
            try {
                const response = await api.metricTypes.searchProductMetrics(query)
                commit(mutationTypes.SET_PRODUCT_METRICS, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        /**
         * @param {ProductsQuery} query
         * @returns {Promise<*>}
         */
        searchProducts: async ({commit, dispatch}, query) => {
            try {
                const response = await api.products.searchProducts(query)
                commit(mutationTypes.SET_PRODUCTS, response)
                return response
            } catch (e) {
                const errorResponse = await dispatch('errors/addErrors', [e], {root: true})
                return Promise.reject(errorResponse[0])
            }
        },
        resetProductBillingFrequencies: ({commit}) => commit(mutationTypes.RESET_PRODUCT_BILLING_FREQUENCIES),
        resetBundleTypes: ({commit}) => commit(mutationTypes.RESET_PRODUCT_BUNDLE_TYPES),
        resetProductCategories: ({commit}) => commit(mutationTypes.RESET_PRODUCT_CATEGORIES),
        resetProductInventoryTypes: ({commit}) => commit(mutationTypes.RESET_PRODUCT_INVENTORY_TYPES),
        resetProductStatuses: ({commit}) => commit(mutationTypes.RESET_PRODUCT_STATUSES),
        resetProductTypes: ({commit}) => commit(mutationTypes.RESET_PRODUCT_TYPES),
        resetProductFamilies: ({commit}) => commit(mutationTypes.RESET_PRODUCT_FAMILIES),
        resetProductMetrics: ({commit}) => commit(mutationTypes.RESET_PRODUCT_METRICS),
        resetProducts: ({commit}) => commit(mutationTypes.RESET_PRODUCTS)
    },
    mutations: {
        [mutationTypes.RESET_PRODUCT_BILLING_FREQUENCIES]: (state) => state.productBillingFrequencies = initialState().productBillingFrequencies,
        [mutationTypes.RESET_PRODUCT_BUNDLE_TYPES]: (state) => state.productBundleTypes = initialState().productBundleTypes,
        [mutationTypes.RESET_PRODUCT_CATEGORIES]: (state) => state.productCategories = initialState().productCategories,
        [mutationTypes.RESET_PRODUCT_INVENTORY_TYPES]: (state) => state.productInventoryTypes = initialState().productInventoryTypes,
        [mutationTypes.RESET_PRODUCT_STATUSES]: (state) => state.productStatuses = initialState().productStatuses,
        [mutationTypes.RESET_PRODUCT_TYPES]: (state) => state.productTypes = initialState().productTypes,
        [mutationTypes.RESET_PRODUCT_FAMILIES]: (state) => state.productFamilies = initialState().productFamilies,
        [mutationTypes.RESET_PRODUCT_METRICS]: (state) => state.productMetrics = initialState().productMetrics,
        [mutationTypes.RESET_PRODUCTS]: (state) => Object.assign(state, initialState()),
        [mutationTypes.SET_PRODUCT_BILLING_FREQUENCIES]: (state, data) => state.productBillingFrequencies = data,
        [mutationTypes.SET_PRODUCT_BUNDLE_TYPES]: (state, data) => state.productBundleTypes = data,
        [mutationTypes.SET_PRODUCT_CATEGORIES]: (state, data) => state.productCategories = data,
        [mutationTypes.SET_PRODUCT_INVENTORY_TYPES]: (state, data) => state.productInventoryTypes = data,
        [mutationTypes.SET_PRODUCT_STATUSES]: (state, data) => state.productStatuses = data,
        [mutationTypes.SET_PRODUCT_TYPES]: (state, data) => state.productTypes = data,
        [mutationTypes.SET_PRODUCT_FAMILIES]: (state, data) => state.productFamilies = data,
        [mutationTypes.SET_PRODUCT_METRICS]: (state, data) => state.productMetrics = data,
        [mutationTypes.SET_PRODUCTS]: (state, data) => {
            const {data: products, pagination} = data

            state.products = {
                data: products.map(mapProduct),
                pagination: mapLegacyPagination(pagination)
            }
        }
    }
}
